import { useLayoutEffect, useState, useCallback, RefObject } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type Size = Omit<DOMRectReadOnly, 'toJSON'>;

const SIZE_INITIAL: Size = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export type useResizeObserverType = Size;

const useResizeObserver = <E extends HTMLElement = HTMLElement>(elementRef: RefObject<E>): useResizeObserverType => {
  const [size, setSize] = useState<Size>(SIZE_INITIAL);

  const handleResize = useCallback(
    (entries: ReadonlyArray<ResizeObserverEntry>) => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }

      const entryContentRect = entries[0].contentRect;
      const newSize: Size = {
        x: entryContentRect.x,
        y: entryContentRect.y,
        width: entryContentRect.width,
        height: entryContentRect.height,
        top: entryContentRect.top,
        right: entryContentRect.right,
        bottom: entryContentRect.bottom,
        left: entryContentRect.left,
      };
      setSize(newSize);
    },
    [setSize],
  );

  useLayoutEffect(() => {
    if (!elementRef.current) {
      return;
    }

    let observer: ResizeObserver | null = new ResizeObserver((entries: ReadonlyArray<ResizeObserverEntry>) => {
      handleResize(entries);
    });
    observer.observe(elementRef.current);

    // eslint-disable-next-line consistent-return
    return (): void => {
      observer?.disconnect();
      observer = null;
    };
  }, [handleResize, elementRef]);

  return size;
};

export default useResizeObserver;
